import React, { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "../../components/dashboard/DashboardLayout";
import { PageHeader, StatusBadge } from "../../components/dashboard/UI";
import { useI18n } from "../../i18n/I18nContext";
import { api } from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Download, AlertTriangle, Cpu, ShieldCheck, Camera, Activity,
} from "lucide-react";

const HEALTH_TONE = (h) =>
  h >= 90 ? "text-[#10B981]" : h >= 75 ? "text-[#19A7E0]" : h >= 60 ? "text-[#F59E0B]" : "text-[#EF4444]";

const Licenses = () => {
  const { t } = useI18n();
  const [licenses, setLicenses] = useState([]);
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/licenses").then((r) => r.data).catch(() => []),
      api.get("/assets").then((r) => r.data).catch(() => []),
    ]).then(([l, a]) => {
      setLicenses(l);
      setAssets(a);
    });
  }, []);

  const hardwareAssets = useMemo(() => assets.filter((a) => a.category === "HARDWARE"), [assets]);
  const physicalAssets = useMemo(() => assets.filter((a) => a.category === "PHYSICAL"), [assets]);

  return (
    <DashboardLayout>
      <PageHeader
        eyebrow={t("dashboard.summary")}
        title={t("dashboard.licenses")}
      />

      <Tabs defaultValue="software" className="w-full" data-testid="assets-tabs">
        <TabsList className="bg-[#0A0D14] border border-white/[0.08] p-1 rounded-sm grid grid-cols-1 sm:grid-cols-3 h-auto gap-1">
          <TabsTrigger
            value="software"
            data-testid="tab-software"
            className="data-[state=active]:bg-[#19A7E0]/15 data-[state=active]:text-[#19A7E0] text-white/70 rounded-sm font-mono uppercase tracking-[0.18em] text-[11px] py-2.5"
          >
            <Cpu className="w-3.5 h-3.5 mr-2" /> {t("dashboard.assetSoftwareTab")}
          </TabsTrigger>
          <TabsTrigger
            value="hardware"
            data-testid="tab-hardware"
            className="data-[state=active]:bg-[#19A7E0]/15 data-[state=active]:text-[#19A7E0] text-white/70 rounded-sm font-mono uppercase tracking-[0.18em] text-[11px] py-2.5"
          >
            <ShieldCheck className="w-3.5 h-3.5 mr-2" /> {t("dashboard.assetHardwareTab")}
          </TabsTrigger>
          <TabsTrigger
            value="physical"
            data-testid="tab-physical"
            className="data-[state=active]:bg-[#19A7E0]/15 data-[state=active]:text-[#19A7E0] text-white/70 rounded-sm font-mono uppercase tracking-[0.18em] text-[11px] py-2.5"
          >
            <Camera className="w-3.5 h-3.5 mr-2" /> {t("dashboard.assetPhysicalTab")}
          </TabsTrigger>
        </TabsList>

        {/* SOFTWARE LICENSES */}
        <TabsContent value="software" className="mt-6" data-testid="panel-software">
          <p className="text-sm text-white/60 mb-4">{t("dashboard.assetSoftwareDesc")}</p>
          <div className="card-sharp overflow-hidden" data-testid="licenses-table">
            <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/[0.08] text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
              <div className="col-span-3">{t("dashboard.product")}</div>
              <div className="col-span-3">Code</div>
              <div className="col-span-1">{t("dashboard.seats")}</div>
              <div className="col-span-2">{t("dashboard.version")}</div>
              <div className="col-span-2">{t("dashboard.expiresAt")}</div>
              <div className="col-span-1">{t("dashboard.status")}</div>
            </div>
            {licenses.map((l) => {
              const expired = new Date(l.expires_at) < new Date();
              const expSoon = !expired && (new Date(l.expires_at) - Date.now() < 60 * 86400 * 1000);
              const fwOutdated = l.version !== l.latest_firmware_version;
              return (
                <div
                  key={l.id}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-4 px-6 py-5 border-b border-white/[0.06] last:border-0 hover:bg-white/[0.02]"
                  data-testid={`license-${l.code}`}
                >
                  <div className="col-span-3">
                    <p className="font-medium">{l.product === "ATLAS" ? "ATLAS System" : "Sygnal Shield"}</p>
                    <p className="text-xs text-white/50">{l.edition}</p>
                  </div>
                  <div className="col-span-3 font-mono text-xs text-[#19A7E0] tracking-wider">{l.code}</div>
                  <div className="col-span-1 font-mono text-sm">{l.seats}</div>
                  <div className="col-span-2 font-mono text-xs">
                    <p>{l.version}</p>
                    {fwOutdated && (
                      <p className="text-[#F59E0B] flex items-center gap-1 mt-1 text-[10px]">
                        <AlertTriangle className="w-3 h-3" /> → {l.latest_firmware_version}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2 font-mono text-xs">
                    {new Date(l.expires_at).toLocaleDateString()}
                  </div>
                  <div className="col-span-1">
                    <StatusBadge
                      status={expired ? "expired" : expSoon ? "expiring" : "active"}
                      label={t(`statuses.${expired ? "expired" : expSoon ? "expiring" : "active"}`)}
                    />
                  </div>

                  {fwOutdated && (
                    <div className="col-span-12 mt-2 flex items-center justify-between bg-[#F59E0B]/[0.06] border border-[#F59E0B]/20 rounded-sm px-3 py-2">
                      <p className="text-xs text-[#F59E0B]">
                        {t("dashboard.latestFirmware")}: <span className="font-mono">{l.latest_firmware_version}</span>
                      </p>
                      <button
                        data-testid={`firmware-download-${l.code}`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 btn-ghost text-[10px] font-mono uppercase tracking-[0.18em] rounded-sm"
                      >
                        <Download className="w-3 h-3" /> {t("dashboard.download")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {licenses.length === 0 && <p className="px-6 py-8 text-sm text-white/50">{t("dashboard.noData")}</p>}
          </div>
        </TabsContent>

        {/* HARDWARE / SIGNAL SHIELD */}
        <TabsContent value="hardware" className="mt-6" data-testid="panel-hardware">
          <p className="text-sm text-white/60 mb-4">{t("dashboard.assetHardwareDesc")}</p>
          <AssetGrid assets={hardwareAssets} t={t} testidPrefix="hardware" />
        </TabsContent>

        {/* PHYSICAL INFRASTRUCTURE */}
        <TabsContent value="physical" className="mt-6" data-testid="panel-physical">
          <p className="text-sm text-white/60 mb-4">{t("dashboard.assetPhysicalDesc")}</p>
          <AssetGrid assets={physicalAssets} t={t} testidPrefix="physical" />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

const AssetGrid = ({ assets, t, testidPrefix }) => {
  if (!assets.length) {
    return <p className="text-sm text-white/50">{t("dashboard.noData")}</p>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {assets.map((a) => (
        <article
          key={a.id}
          className="card-sharp p-5"
          data-testid={`${testidPrefix}-asset-${a.code}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#19A7E0]">{a.code}</p>
              <h3 className="mt-1 text-base font-medium tracking-tight truncate">{a.name}</h3>
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mt-1">
                {a.model} · {a.site}
              </p>
            </div>
            <StatusBadge
              status={a.status === "active" ? "active" : a.status === "alert" ? "open" : "maintenance"}
              label={t(`statuses.${a.status === "alert" ? "open" : a.status}`)}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06]">
            <div className="bg-[#0A0D14] px-3 py-2.5">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
                {t("dashboard.health")}
              </p>
              <p className={`mt-1 font-mono text-base ${HEALTH_TONE(a.health)}`}>{a.health}%</p>
            </div>
            <div className="bg-[#0A0D14] px-3 py-2.5">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
                {t("dashboard.serial")}
              </p>
              <p className="mt-1 font-mono text-xs truncate">{a.serial}</p>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-[11px] font-mono text-white/40">
            <span className="flex items-center gap-1.5">
              <Activity className="w-3 h-3" />
              {t("dashboard.lastService")}: {new Date(a.last_service_at).toLocaleDateString()}
            </span>
            <span>
              {t("dashboard.installedAt")}: {new Date(a.installed_at).toLocaleDateString()}
            </span>
          </div>
        </article>
      ))}
    </div>
  );
};

export default Licenses;
